import { formatAppDateTime } from "../../app/utils/appTimeFormat";

const QUEUE_PALETTE = [
  "#06b6d4",
  "#10b981",
  "#8b5cf6",
  "#f59e0b",
  "#ec4899",
  "#6366f1",
  "#14b8a6",
  "#ef4444",
];

export function formatJobTimeLeft(ms) {
  if (ms == null || Number.isNaN(ms)) return "—";
  const x = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(x / 86400);
  const h = Math.floor((x % 86400) / 3600);
  const m = Math.floor((x % 3600) / 60);
  const s = x % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function formatLocalStamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return formatAppDateTime(d);
}

export function jobMsUntilNext(job, now = Date.now()) {
  if (job.msUntilNext != null) return Math.max(0, job.msUntilNext);
  if (!job.nextRunAt) return null;
  return Math.max(0, new Date(job.nextRunAt).getTime() - now);
}

/** Compact job row subline (queue · schedule · next run). */
export function jobLedgerSubline(job, now = Date.now()) {
  const parts = [job?.queue || "default"];
  if (job?.cron) parts.push(job.cron);
  else parts.push("Manual");
  const ms = jobMsUntilNext(job, now);
  if (job?.cron && ms != null) parts.push(`in ${formatJobTimeLeft(ms)}`);
  if (job?.nextRunAt) parts.push(formatLocalStamp(job.nextRunAt));
  return parts.join(" · ");
}

export function queueAccent(queueName) {
  const name = String(queueName || "default");
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return QUEUE_PALETTE[Math.abs(hash) % QUEUE_PALETTE.length];
}

export function groupJobsByQueue(jobs = []) {
  const map = new Map();
  jobs.forEach((job) => {
    const key = job.queue || "default";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(job);
  });
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([queue, items]) => ({
      queue,
      accent: queueAccent(queue),
      jobs: items.sort((a, b) => {
        const aMs = jobMsUntilNext(a) ?? Number.MAX_SAFE_INTEGER;
        const bMs = jobMsUntilNext(b) ?? Number.MAX_SAFE_INTEGER;
        return aMs - bMs;
      }),
    }));
}

export function getUpcomingJobs(jobs = [], limit = 6, now = Date.now()) {
  return jobs
    .map((job) => ({ job, ms: jobMsUntilNext(job, now) }))
    .filter((row) => row.ms != null)
    .sort((a, b) => a.ms - b.ms)
    .slice(0, limit)
    .map((row) => row.job);
}

export function countHealthySchedulers(jobs = []) {
  return jobs.filter((job) => job.cron && job.nextRunAt && !job.scheduleNote).length;
}

export function filterJobs(jobs = [], { queue = "all", search = "" }) {
  const q = search.trim().toLowerCase();
  return jobs.filter((job) => {
    if (queue !== "all" && job.queue !== queue) return false;
    if (!q) return true;
    const hay = [job.name, job.queue, job.description, job.cron, job.scheduleNote]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}

export function listQueues(jobs = []) {
  return [...new Set(jobs.map((job) => job.queue || "default"))].sort();
}
